/**
 * Analytics Tracking Service — LGPD-safe
 *
 * Tracks behavioural events with a SHA-256 hashed user_id.
 * Fire-and-forget: never blocks UI, never throws.
 * Only inserts into public.analytics_events (admin-read-only via RLS).
 */

/** Cliente por `import()`: analytics nunca é caminho crítico de renderização. */
const cliente = async () => (await import("@/integrations/supabase/client")).supabase;
import { getSourceAngle, getAngleLabel } from "@/lib/utmCapture";
import { paramsParaTerceiros } from "@/lib/analytics/paramsParaTerceiros";
import { gaEvent } from "@/lib/ga4";
import { getPlatform } from "@/lib/platform";
import { planoParaEventos } from "@/lib/analytics/planSnapshot";

/* ── Types ── */

export type AnalyticsEventName =
  | "app_open"
  | "eu360_open"
  // [02/11/2026] Jornada de 3 passos do /perfil (flag perfilJornadaV1).
  | "perfil_jornada_shown"
  | "perfil_jornada_step_clicked"
  | "perfil_jornada_step_done"
  | "perfil_mais_expanded"
  | "questionario_started"
  | "questionario_completed"
  | "meu_dia_open"
  | "myday_save"
  | "myday_first_save"
  | "audio_started"
  | "audio_paused"
  | "audio_resumed"
  | "audio_completed"
  | "audio_abandoned"
  | "enquete_voted"
  | "inspiracao_opened"
  | "material_opened"
  | "rotina_completed"
  | "selo_unlocked"
  | "subscription_started"
  | "subscription_cancel_confirmed"
  | "save_flow_step_declined"
  | "materna_box_viewed"
  | "tab_switched"
  | "filter_applied"
  | "search_performed"
  | "trilha_atipica_step_opened"
  // Assistant events
  | "assistant_question_asked"
  | "assistant_response_generated"
  | "assistant_checkin_selected"
  | "assistant_share_clicked"
  | "assistant_insight_detected"
  | "assistant_insight_shared"
  | "assistant_insight_reacted"
  | "assistant_guide_shown"
  | "assistant_guide_clicked"
  | "assistant_action_clicked"
  | "assistant_triage_intent"
  | "assistant_triage_cta_clicked"
  | "assistant_proactive_greeting_injected"
  | "assistant_proactive_insight_shown"
  | "assistant_memories_opened"
  | "assistant_memory_deleted"
  | "assistant_memories_cleared"
  | "assistant_messages_cleared"
  // FAB, teaser de pergunta sugerida, fluxos guiados e cartão de crise.
  // Emitidos desde 2026, mas só agora tipados — usavam `as any`.
  | "assistant_fab_shown"
  | "assistant_fab_opened"
  | "assistant_fab_revoked_warning_opened"
  | "assistant_fab_restore_clicked"
  // Aviso de superfície de IA desligada por revogação (21/08/2026).
  | "ai_disabled_notice_shown"
  | "ai_disabled_notice_settings_clicked"
  | "assistant_suggested_question_shown"
  | "assistant_suggested_question_clicked"
  | "assistant_suggested_question_dismissed"
  | "assistant_suggested_question_autosent"
  // Bloco de entrada da assistente no /perfil (redesenho 13/08/2026)
  | "assistant_prompt_card_shown"
  | "assistant_prompt_card_submitted"
  | "assistant_guided_flow_started"
  | "assistant_guided_flow_completed"
  | "assistant_empty_state_shortcut_clicked"
  | "assistant_crisis_card_shown"
  | "assistant_crisis_channel_clicked"
  | "assistant_appstore_nudge_click"
  // Clique em badge de loja na landing pública (metadata.store = ios | android,
  // metadata.slot = hero | onde_usar | footer | barra).
  | "landing_store_click"
  // Abertura do DailyShortcutsModal em qualquer atalho (assistente, agenda,
  // diario, momento). Nenhum dos 4 caminhos de abertura emitia evento — o
  // assistant_fab_opened cobria só o FAB e subcontava o assistente.
  // metadata.key = atalho aberto; metadata.source = tile | deep_link |
  // daypart_autoopen | context_bridge. `module` repete a key para dar quebra
  // nativa na tabela e para não colidir no throttle de 2s do trackAnalytics.
  | "shortcut_modal_opened"
  // Ritual events
  | "ritual_try_today"
  | "ritual_show_other"
  // Onboarding
  | "onboarding_suggestion_shown"
  | "onboarding_suggestion_clicked"
  // Child Development Map
  | "child_dev_map_started"
  | "child_dev_map_completed"
  | "child_dev_evolution_viewed"
  | "child_dev_waze_viewed"
  | "child_dev_waze_assistant_cta_clicked"
  | "child_dev_alert_created"
  | "child_dev_alert_opened"
  | "child_dev_alert_cta_clicked"
  | "dev_daily_idea_clicked"
  | "child_dev_forecast_viewed"
  | "child_dev_twin_built"
  | "child_dev_patterns_detected"
  | "child_dev_benchmark_viewed"
  // Notebook
  | "child_dev_notebook_viewed"
  | "child_dev_notebook_timeline_opened"
  // Shareability
  | "devmap_shared"
  | "chaos_map_shared"
  // Partner Mode
  | "partner_mode_shared"
  | "partner_link_generated"
  // Progress
  | "progresso_open"
  // HelpFAB (menu flutuante de ajuda) — emitidos desde 2026 com `as any`.
  | "help_fab_opened"
  | "help_fab_navigate"
  | "help_fab_reset_guides"
  // Post-activation onboarding
  | "post_activation_shown"
  | "post_activation_cta_clicked"
  // Calendar
  | "calendar_google_sync"
  | "calendar_ics_export"
  // Community / Moderation
  | "community_story_viewed"
  | "community_story_reacted"
  | "community_story_reported"
  // Monetization funnel
  | "upgrade_modal_shown"
  | "upgrade_completed"
  // FIX2 TODO#2 — quota gating signals (server-side enforced)
  | "receitas_quota_blocked"
  // LGPD
  | "lgpd_consent_given"
  | "ai_consent_revoked"
  | "ai_consent_restored"
  | "ai_analysis_consent_given"
  | "ai_analysis_consent_revoked"
  | "ai_analysis_card_dismissed"
  | "privacy_policy_update_notice_seen"
  | "health_log_consent_accepted"
  | "health_log_consent_declined"
  | "health_log_consent_revoked"
  // (28/08/2026) Os eventos medicacao_* saíram junto com a feature.


  | "lgpd_data_export_requested"
  | "lgpd_account_delete_requested"
  | "cookie_consent_decision"
  // Retention signals
  | "push_permission_granted"
  | "push_register_failed"
  | "push_register_retry_ok"
  | "push_permission_denied"
  // (03/11/2026, 1.2.16) Estado real da permissão (inclui provisional do iOS)
  // e tela "reativar avisos" para quem negou.
  | "push_permission_status"
  | "push_reenable_shown"
  | "push_reenable_clicked"
  | "push_reenable_dismissed"
  | "push_reenabled"
  | "push_ios_blocked_reason"
  | "push_prompt_post_checkin_shown"
  | "push_prompt_post_checkin_accepted"
  | "push_prompt_post_checkin_dismissed"
  | "push_prompt_post_agenda_shown"
  | "push_prompt_post_agenda_accepted"
  | "push_prompt_post_agenda_failed"
  | "push_prompt_banner_shown"
  | "push_prompt_banner_accepted"
  | "push_prompt_banner_dismissed"
  | "push_prompt_banner_reopened_2strike"
  // Campanha "Materna360 nas duas lojas" (ago/2026) — banner web no /perfil
  | "stores_launch_banner_shown"
  | "stores_launch_banner_dismissed"
  | "stores_launch_banner_clicked"
  | "push_prompt_golden_path_shown"
  | "push_prompt_golden_path_accepted"
  | "push_prompt_golden_path_dismissed"
  // Melhoria 2 (06/2026) — Push victory modal pós-vitória (4 triggers:
  // emotional_context_completed, first_checkin, assistant_fab_opened, myday_first_save).
  | "push_victory_prompt_shown"
  | "push_victory_prompt_accepted"
  | "push_victory_prompt_dismissed"
  // Convite suprimido antes de aparecer, por overlay de prioridade maior
  // (ex.: chat aberto pelo spotlight, 1120). A flag de uso único NÃO é
  // consumida — sem este evento o adiamento seria invisível.
  | "push_victory_prompt_deferred"
  // NPS in-app
  | "nps_shown"
  | "nps_answered"
  | "nps_answer_failed"
  | "nps_dismissed"
  // Carta noturna — pedido de opt-in explícito (24/08/2026)
  | "nightly_letter_prompt_shown"
  | "nightly_letter_prompt_accepted"
  | "nightly_letter_decision_failed"
  | "nightly_letter_prompt_declined"

  // Force-close por overlay de prioridade maior — não é "não" da usuária
  | "nps_interrupted"
  | "leap_forecast_viewed"
  | "partner_dashboard_viewed"
  | "weekly_insights_viewed"
  // Bem-estar (Ritual)
  | "bem_estar_whisper_checkin_clicked"
  | "wellbeing_suggestion_generated"
  | "wellbeing_suggestion_started"
  | "wellbeing_suggestion_swapped"
  // Home /perfil — O que cabe agora
  | "oque_cabe_agora_open"
  | "checkin_saved"
  // Organizador de Rotina (Etapa 0, sem IA) — 13/08/2026
  /**
   * Impressão REAL do card no /perfil (03/09/2026): dispara quando o card entra
   * em viewport, uma vez por sessão. `rotina_base_card_shown` NÃO é impressão —
   * ele só dispara quando o wizard de 8 passos abre, e desde 21/08 o rosto do
   * card abre o caminho escrito. Sem este evento não há denominador.
   */
  | "rotina_base_card_impression"
  | "rotina_base_card_shown"
  | "rotina_base_card_started"
  | "rotina_base_answered"
  | "rotina_base_draft_shown"
  | "rotina_base_dismissed"
  | "rotina_base_saved"
  | "rotina_base_edited"
  // Organizador de Rotina escrito (Bloco C, IA) — 21/08/2026
  | "rotina_escrita_opened"
  | "rotina_escrita_proposta_shown"
  | "rotina_escrita_perguntas_shown"
  | "rotina_escrita_perguntas_answered"
  | "rotina_escrita_pontual_shown"
  | "rotina_escrita_pontual_saved"
  /* Texto dela era sobre remédio e o organizador recusou — o app não guarda
     horário de medicação desde 28/08/2026. Sem nome, sem dose. */
  | "rotina_escrita_medicacao_recusada"
  | "rotina_escrita_pontual_discarded"

  | "rotina_sugestao_brincadeira_shown"
  | "rotina_fase_sugestoes_shown"
  | "rotina_fase_sugestao_accepted"
  | "rotina_fase_sugestao_discarded"
  /* Ponte do bloco "nessa fase" para o hub de conteúdo (Brincar, Receitas,
     Conexão, Respiro). É a métrica que diz se o bloco está funcionando como
     porta de entrada do catálogo, e não só como gerador de item. */
  | "rotina_fase_hub_opened"
  /* Sugestões de VÃO (22/08/2026): o que cabe no buraco entre duas coisas da
     rotina — conexão com quem ficou em casa, lição, cardápio, mochila, respiro.
     `familia` no metadata diz qual delas ela aceita, e é isso que decide quais
     famílias ficam. */
  | "rotina_vao_sugestoes_shown"
  | "rotina_vao_sugestao_accepted"
  | "rotina_vao_sugestao_discarded"
  | "rotina_vao_hub_opened"
  /* Faixa inline (22/08/2026): a sugestão de vão passou a viver ENTRE os dois
     itens a que se refere, colapsada. Este evento é o denominador do bloco —
     sem ele, "aceitou" ficaria sem "chegou a abrir". */
  | "rotina_vao_inline_opened"
  | "rotina_vao_nota_opened"
  | "rotina_vao_nota_saved"
  | "rotina_bebe_esqueleto_shown"
  | "rotina_bebe_esqueleto_accepted"
  | "rotina_bebe_esqueleto_dismissed"

  /* Ritmo do bebê (22/08/2026): mamada e soneca por REGISTRO, não por relógio.
     Com bebê ela não crava horário — o que mede valor aqui é ela voltar a
     tocar, e se chega a pedir aviso da janela estimada. */
  | "bebe_ritmo_registrado"
  | "bebe_ritmo_aviso_criado"


  /* Atalho de descoberta para o bloco de fase, que continua no fim da proposta:
     mede se ela desce até lá por vontade própria ou só pelo empurrão. */
  | "rotina_fase_atalho_clicked"



  | "rotina_sugestao_brincadeira_opened"
  | "rotina_sugestao_brincadeira_trocada"

  | "rotina_escrita_item_edit_opened"
  | "rotina_sugestao_brincadeira_accepted"
  | "rotina_sugestao_brincadeira_discarded"
  // "Me conte mais": ela acrescenta o que faltou e o organizador SOMA à
  // proposta — três âncoras não fazem uma rotina inteira.
  | "rotina_escrita_complemento_opened"
  | "rotina_escrita_complemento_applied"
  | "rotina_escrita_saved"
  | "rotina_escrita_alcance_changed"
  | "rotina_escrita_dismissed"
  | "rotina_escrita_failed"
  // Pedido de push DENTRO do organizador, logo depois de salvar a rotina —
  // é o momento em que o aviso acabou de ganhar sentido para ela.
  | "rotina_escrita_push_shown"
  | "rotina_avisos_ligados"
  | "rotina_avisos_recusados"
  | "rotina_avisos_ajustar_aberto"
  | "rotina_escrita_push_accepted"
  | "rotina_escrita_push_denied"
  | "rotina_escrita_push_dismissed"
  | "rotina_escrita_advance_set"
  | "rotina_escrita_digest_suggested"

  // Aposentado em 08/2026: o Ritual deixou de abrir modal automaticamente.
  // O nome fica na união porque o histórico existe no banco.
  | "ritual_autoopened"
  // Substituto: o card do Ritual passa a se destacar inline quando não houve
  // check-in no dia. 1x/dia, mesma cadência do evento acima — é o denominador
  // que torna a comparação antes/depois possível contra checkin_saved.
  | "ritual_destaque_shown"
  // F2.1 — Contexto emocional (Sprint 0 Retenção)
  | "emotional_context_completed"
  // F3 — Slot rotativo de novidade no /perfil (Sprint 0 Retenção)
  | "perfil_novelty_seen"
  // Mapa do Desenvolvimento — Paywall por dimensão (Free vs Essencial+)
  | "mapa_dimensao_bloqueada_clicada"
  | "mapa_upsell_banner_clicado"
  | "mapa_completo_visto"
  // Unified gating signal (Passo 4 / Fase 1A) — emitted client-side when a
  // user attempts to use a feature they're not allowed to. Module = featureKey.
  | "gate_blocked"
  // Paywall full-screen (23/05/2026) — disparado nos 3 triggers de dor:
  // extra_child, mapa_refazer, biblioteca_premium.
  | "paywall_shown"
  // 26/08/2026 — impressão PASSIVA de upsell (vitrine do /perfil, popup
  // contextual, prateleira do Hero Moment). Antes usavam `paywall_shown`, o que
  // fazia o denominador do funil ser "aberturas do /perfil": 114 mães de
  // exibição passiva contra 22 de interrupção real em 8 dias.
  // Ver src/lib/paywallIntent.ts → displayEventForTrigger().
  | "upsell_impression"
  | "paywall_cta_clicked"

  // 20/08/2026 — separação de intenção. `upsell_surface_clicked` = descoberta
  // (a oferta interrompeu a mãe: blur, contador, card bloqueado).
  // `paywall_cta_clicked` = decisão de assinar. Ver src/lib/paywallIntent.ts.
  | "upsell_surface_clicked"
  | "paywall_dismissed"
  // Hero Moment (nova seção premium — 22/07/2026)
  | "hero_moment_opened"
  // Desafios de 7 dias (13/08/2026)
  | "desafio_opened"
  | "desafio_dia_completed"
  | "desafio_completed"
  // 14/08/2026: leitura sem marcação e abandono (metadata.dias_parada)
  | "desafio_dia_aberto"
  | "desafio_abandoned"
  // Funil de planos/checkout (Front 1 — instrumentação 07/06/2026)
  | "planos_view"
  | "planos_opened"
  // 26/08/2026 — a perda invisível do funil. `planos_opened` sai do
  // useOpenPlanos junto do clique; `planos_view` só sai se a /planos montar.
  // Medido em 8 dias: 15 mães navegaram, 7 chegaram. Nada registrava o desvio.
  //   - `planos_nav_landed` → chegou (metadata.elapsed_ms desde a navegação)
  //   - `planos_nav_lost`   → não chegou (metadata.final_path + reason:
  //     protected_redirect | other)
  | "planos_nav_landed"
  | "planos_nav_lost"
  // 27/08/2026 — as saídas, que nunca tiveram evento. `paywall_dismissed` ganhou
  // `metadata.via` (close_button | agora_nao | backdrop_esc); estes dois cobrem
  // o resto do caminho:
  //   - `planos_abandoned` → saiu da /planos sem comprar (metadata.interacted,
  //     dwell_ms) — inclui unmount sem nenhum clique
  //   - `paywall_store_sheet_cancelled` → desistiu no sheet da loja (nativo)
  | "planos_abandoned"
  | "paywall_store_sheet_cancelled"
  | "checkout_started"

  // Falha entre checkout_started e o redirect pro Stripe. metadata.reason:
  // create_checkout_error | test_account_blocked | missing_url |
  // native_subscription_active | unexpected_error. Antes desta instrumentação
  // o funil web tinha topo e fundo, mas nenhum sinal de perda no meio.
  | "checkout_failed"
  | "checkout_native_guard_unverified"
  | "paywall_recovery_sent"
  /**
   * [item 3 · 05/09/2026] O sistema não conseguiu ler a assinatura e liberou o
   * acesso. NÃO é estado da mãe — é métrica de infraestrutura: quantas vezes
   * por dia a leitura falha. metadata.reason: edge_error | edge_no_tier |
   * auth_required | invalid_token | no_email | client_exception.
   * metadata.attempt = nº da tentativa falhada seguida (espera crescente).
   * Sem este evento, "liberar no escuro" é invisível.
   */
  | "plan_read_unresolved"
  // Upsell proativo em momentos de valor (Ideia #40 backlog) — 3 gatilhos:
  // atipico_3rd, assistente_3rd, diario_3rd. Persistência: user_upsell_shown.
  | "contextual_upsell_shown"
  | "contextual_upsell_clicked"
  | "contextual_upsell_dismissed"
  // Sprint 1 — Ativação (funil tráfego pago → 1ª sessão → PAS)
  | "first_perfil_view"
  | "first_session_spotlight_shown"
  | "first_session_spotlight_cta"
  | "first_session_spotlight_dismissed"
  // Saída leve do SessionGreeting/Spotlight (backdrop ou botão voltar): fecha
  // sem gravar a flag durável de dismiss. metadata.source = backdrop | back_button.
  | "session_greeting_soft_closed"
  // Card da assistente que reabre a cada sessão de navegador. Idem: emitidos
  // desde 2026 com `as any`, agora tipados.
  | "session_greeting_shown"
  | "session_greeting_cta"
  | "session_greeting_shortcut"
  | "session_greeting_dismissed"
  | "first_meaningful_action"
  // Quick notes & context bridges (Rotina/Conexão — inline UX)
  | "quick_note_saved"
  | "context_bridge_clicked"
  | "post_save_feedback"
  /**
   * [14/09/2026] Falha da gravação de atribuição. A RPC `record_user_attribution`
   * NÃO lança em erro de banco — devolve `{ error }` —, então o try/catch em volta
   * dela nunca via nada e a linha simplesmente não existia. É a mesma classe do
   * bug de `guest_agendas`: número menor no painel, sem nenhum sinal de falha.
   * Sem este evento não há como distinguir "não veio de anúncio" de "não gravou".
   */
  | "attribution_record_failed"
  // Nome devolvido pela Apple só na 1ª autorização (14/09/2026)
  | "apple_name_persist_failed"
  // Bloco 2 lançamento — eventos canônicos espelhados no GA4
  | "signup_completed"
  | "onboarding_completed"
  | "onboarding_offer_shown"
  | "onboarding_offer_cta_clicked"
  | "onboarding_offer_dismissed"
  | "brincadeira_do_dia_feita"
  | "brincadeira_do_dia_sem_catalogo"
  | "daily_play_auto_enabled"
  | "daily_play_invite_shown"
  | "daily_play_invite_accepted"
  | "daily_play_invite_dismissed"
  | "daily_play_disabled"
  | "push_prompt_daily_play_shown"
  | "push_prompt_daily_play_accepted"
  | "push_prompt_daily_play_dismissed"
  | "push_prompt_daily_play_failed"
  | "first_checkin"
  | "purchase"
  | "purchase_cancelled"
  | "purchase_failed"
  | "store_offerings_diagnostic"
  // Onboarding obrigatório + Momento Wow (06/2026)
  | "onboarding_step_started"
  | "onboarding_step_completed"
  | "onboarding_abandoned"
  | "wow_flow_started"
  | "wow_assistant_shown"
  | "wow_assistant_question_asked"
  | "wow_assistant_completed"
  | "wow_assistant_error"
  | "assistant_crisis_shortcircuit"
  /**
   * [Lote 3 — 20/08/2026] `wow_flow_completed` fica congelado (histórico).
   * O fluxo de 1 tela emite `wow_flow_completed_v2`. Saíram de vez:
   * wow_letter_shown/completed e wow_plan_shown/generated/completed.
   */
  | "wow_flow_completed"
  | "wow_flow_completed_v2"
  | "wow_abandoned_at_step"
  | "wow_consent_shown"
  | "wow_consent_accepted"
  | "wow_consent_declined"
  | "first_victory_achieved"
  // Item de hoje, sob demanda (12/08/2026) — geração só com pedido explícito
  | "plan_item_requested"
  | "plan_item_shown"
  | "plan_item_saved"
  | "plan_item_regenerated"
  | "plan_item_lint_rejected"
  | "plan_item_quota_blocked"

  // Legacy letters — templates do empty state (Cartas pro Futuro)
  | "legacy_template_open"
  // WhatsApp channel — Fase 1 click-to-chat (19/06/2026)
  | "whatsapp_click"
  // Sprint 4 — Perfil Atípico (20/06/2026)
  | "atypical_markers_saved"
  | "atypical_disclaimer_accepted"
  | "atypical_disclaimer_dismissed"
  | "assistant_clinical_topic_blocked"
  | "atypical_ritual_opened"
  | "atypical_ritual_step"
  | "atypical_ritual_completed"
  | "brincadeira_atypical_match_clicked"
  // Lote 3 (E1+E2) — instrumentação baseline Lembretes (72h) para dimensionar
  // impacto do bug do skeleton eterno antes de aplicar B1. Ver AdminLembretesBaseline.
  | "lembretes_fetch_started"
  | "lembretes_fetch_succeeded"
  | "lembretes_fetch_failed"
  | "lembretes_fetch_timeout"
  | "lembretes_surface_viewed"
  // Onda C — coordenação dos hooks de upsell contextual (2026-07)
  | "diary_entry_saved"
  // 19/08/2026 — separação na origem. `lembrete_created` é SÓ ação da mãe;
  // gravação automática (plano do Wow) emite `lembrete_autocreated`.
  // Motivo: o filtro em consumidor depende de alguém lembrar, e o levantamento
  // das 350 mostrou o custo de esquecer (50% de "penetração" era onboarding).
  // Ver docs/memory/notes/lembrete-created-contaminado-2026-08-19.md.
  | "lembrete_created"
  | "lembrete_autocreated"
  | "lembrete_context_opened"
  | "momento_saved"
  // Fase 2 — PlanoDaSemanaCard no /perfil (2026-07-29).
  // Forward-only: mede ativação a partir daqui sem redefinir eventos históricos.
  // wow_item_completed_first tem guarda anti-dupla-contagem via RPC has_completed_first_wow_item
  // (servidor) + LS (cliente). Pode conviver com first_victory_achieved (mantido).
  | "plano_card_shown"
  | "plano_item_completed"
  | "ritual_after_plano_shown"
  | "wow_item_completed_first"
  // Passo "Primeiro compromisso" do cadastro (02/11/2026). O metadata
  // `chip` (pediatra/vacina/...) é dado de saúde do filho: fica SÓ no
  // analytics interno — fora da allowlist de terceiros e do mapa Firebase,
  // então GA4/Firebase recebem no máximo o nome do evento, e a Meta nada
  // recebe (canal dela é só chamada explícita).
  | "onboarding_first_event_shown"
  | "onboarding_first_event_saved"
  | "onboarding_first_event_skipped"
  | "onboarding_push_ask_shown"
  | "onboarding_push_ask_accepted"
  | "onboarding_push_ask_declined";

interface TrackParams {
  event: AnalyticsEventName;
  screen?: string;
  module?: string;
  childAge?: number | null;
  metadata?: Record<string, unknown>;
  /**
   * Aguarda a gravação antes de resolver. Use quando houver navegação logo
   * em seguida (ex.: redirect pro Stripe): sem isto o insert fica em voo e a
   * navegação o cancela, perdendo o evento sem qualquer sinal.
   * Default false — o comportamento fire-and-forget continua sendo a regra.
   */
  flush?: boolean;
}

/* ── SHA-256 hash (browser-native, async) ── */

let cachedHash: string | null = null;
let cachedUid: string | null = null;

export async function hashUserId(uid: string): Promise<string> {
  if (cachedUid === uid && cachedHash) return cachedHash;

  const encoder = new TextEncoder();
  const data = encoder.encode(uid);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hex = hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");

  cachedUid = uid;
  cachedHash = hex;
  return hex;
}

/* ── Throttle: max 1 event per name per 2s ── */

const recentEvents = new Map<string, number>();
const THROTTLE_MS = 2_000;

/** Teto do `flush`: passou disso, o fluxo segue sem esperar a gravação. */
const FLUSH_TIMEOUT_MS = 2_000;

function isThrottled(eventName: string): boolean {
  const now = Date.now();
  const last = recentEvents.get(eventName);
  if (last && now - last < THROTTLE_MS) return true;
  recentEvents.set(eventName, now);
  return false;
}

/* ── Firebase/GA4 nativo: pouquíssimos eventos, de propósito ── */

/**
 * Build 1.2.4 — Item 2. O Firebase recebia só identidade; não havia um único
 * `logEvent` no repositório, então o Google Ads não tinha por onde otimizar.
 *
 * A lista é curta de propósito. Espelhar o `analytics_events` inteiro foi o que
 * produziu "1.187 contra 37" — dois sistemas contando o mesmo funil com
 * definições diferentes. Compra/trial NÃO entram: chegam server-side pela
 * integração RevenueCat → Firebase.
 *
 * `first_agenda_item` é o evento de ativação, e a escolha é medida. Retorno
 * D+1..7 das contas dos últimos 60 dias, por ação na primeira sessão:
 *   base geral ............ 105/2008 = 5,2%
 *   onboarding_completed ... 83/1320 = 6,3%
 *   checkin_saved .......... 19/164  = 11,6%
 *   lembrete_created ....... 40/277  = 14,4%  ← 2,8x a base
 * Salvar o primeiro item de agenda é o que melhor separa quem volta.
 */
/**
 * [17/09/2026] Passos do onboarding entram no GA4 pelo MESMO caminho do
 * `sign_up` — mapa abaixo + `mirrorToFirebase`. Nenhum mecanismo novo.
 *
 * Os eventos internos já existiam (`onboarding_step_started` /
 * `onboarding_step_completed`, desde 07/2026); o que faltava era o espelho no
 * GA4. Por isso o mapa traduz, não duplica.
 *
 * ⚠ Parâmetro é RÓTULO FIXO. `step_name` sai da allowlist abaixo; qualquer id
 * fora dela vira "outro". Nada de nome, e-mail, nome/idade de criança ou trecho
 * do texto que a mãe escreveu.
 */
const ROTULOS_DE_PASSO: Record<string, string> = {
  welcome: "boas_vindas",
  nome: "nome",
  identidade: "identidade",
  sentimento: "como_esta",
  desafios: "desafios",
  filho_data: "data_nascimento",
  filho_nome: "apelido_filho",
  momentos: "momentos_do_dia",
  ajudas: "ajudas",
};

function rotuloDoPasso(metadata: Record<string, unknown>): string {
  const bruto = typeof metadata.question === "string" ? metadata.question : "";
  return ROTULOS_DE_PASSO[bruto] ?? ROTULOS_DE_PASSO.welcome ?? "outro";
}

type ParamsGA4 = Record<string, string | number | boolean>;

const FIREBASE_BUSINESS_EVENTS: Record<
  string,
  {
    ga4Name: string;
    onlyFirst?: boolean;
    params?: (metadata: Record<string, unknown>) => ParamsGA4;
  }
> = {
  // `signup_completed` NÃO entra mais aqui (12/10/2026): o `sign_up` sai de
  // `src/lib/conversionEvents.ts`, uma vez por usuária, com `method` — sem
  // depender da gravação em `analytics_events`.
  // ativação: primeiro item de agenda salvo pela mãe (nunca o auto-criado)
  lembrete_created: { ga4Name: "first_agenda_item", onlyFirst: true },
  // primeira tela do onboarding (welcome, ou o 1º passo de quem chega com agenda)
  onboarding_step_started: { ga4Name: "onboarding_start", onlyFirst: true },
  // cada passo concluído
  onboarding_step_completed: {
    ga4Name: "onboarding_step",
    params: (m) => ({
      step_name: rotuloDoPasso(m),
      step_number: Number.isFinite(Number(m.step_index)) ? Number(m.step_index) : 0,
    }),
  },
};

const FIREBASE_ONCE_PREFIX = "m360:fb_evt:";

/**
 * Espelha um evento de funil no GA4 (web via gtag, nativo via Firebase).
 * Usado também pelo caminho anônimo (`preauthEvents`), que não tem sessão e
 * por isso não passa pelo `trackAnalytics`.
 */
export function espelharEventoDeFunilGA4(ga4Name: string, params: ParamsGA4 = {}): void {
  try {
    const platform = getPlatform();
    gaEvent(ga4Name, { platform, ...params });
    void import("@/integrations/firebase/analytics")
      .then(({ logFirebaseEvent }) => logFirebaseEvent(ga4Name, { platform, ...params }))
      .catch(() => {});
  } catch {
    // never throw — instrumentação
  }
}

async function mirrorToFirebase(
  event: string,
  platform: string,
  metadata: Record<string, unknown> = {},
): Promise<void> {
  try {
    const conf = FIREBASE_BUSINESS_EVENTS[event];
    if (!conf) return;
    if (conf.onlyFirst) {
      const key = `${FIREBASE_ONCE_PREFIX}${conf.ga4Name}`;
      if (localStorage.getItem(key)) return;
      localStorage.setItem(key, "1");
    }
    // Web (gtag) e nativo (Firebase) recebem o MESMO nome canônico.
    espelharEventoDeFunilGA4(conf.ga4Name, conf.params?.(metadata) ?? {});
  } catch {
    // never throw — instrumentação
  }
}

/* ── Public API ── */


/**
 * Track an analytics event. Fire-and-forget.
 * Safe to call anywhere — never throws, never blocks.
 */
export async function trackAnalytics(params: TrackParams): Promise<void> {
  try {
    // Throttle key includes module so two distinct features blocking in the
    // same second don't drop one event (Passo 4 / Mudança 1).
    const throttleKey = params.module ? `${params.event}::${params.module}` : params.event;
    if (isThrottled(throttleKey)) return;

    const {
      data: { session },
    } = await (await cliente()).auth.getSession();
    if (!session?.user?.id) return;

    const userHash = await hashUserId(session.user.id);

    // Enrich with GTM source angle
    const isOnboardingOffer = params.event === "onboarding_offer_shown"
      || params.event === "onboarding_offer_cta_clicked" || params.event === "onboarding_offer_dismissed";
    const isDailyPlay = params.event.startsWith("brincadeira_do_dia_") || params.event.startsWith("push_prompt_daily_play_") || params.event.startsWith("daily_play_");
    const sourceAngle = isOnboardingOffer || isDailyPlay ? null : getSourceAngle();
    const enrichedMetadata = isOnboardingOffer ? {
      platform: getPlatform(),
      intro_offer: typeof params.metadata?.intro_offer === "boolean" ? params.metadata.intro_offer : null,
      appearance: params.metadata?.appearance === 2 ? 2 : 1,
    } : isDailyPlay ? {
      platform: getPlatform(),
      ...(params.event === "brincadeira_do_dia_sem_catalogo" && typeof params.metadata?.faixa === "string" ? { faixa: params.metadata.faixa } : {}),
      // [03/11/2026] Eventos daily_play_*: só plataforma e origem (texto curto, sem dado pessoal).
      ...(params.event.startsWith("daily_play_") && typeof params.metadata?.origem === "string" ? { origem: params.metadata.origem.slice(0, 32) } : {}),
    } : {
      ...(params.metadata ?? {}),
      ...(sourceAngle ? { source_angle: getAngleLabel(sourceAngle) } : {}),
    };
    // [02/11/2026] Plano no momento do evento, em TODO evento e só aqui
    // (tabela interna). Vem depois do spread: quem chama não sobrescreve.
    // Terceiros recebem params.metadata filtrado, nunca enrichedMetadata.
    const { plan, in_trial } = planoParaEventos();
    Object.assign(enrichedMetadata, { plan, in_trial });

    // Etapa A (2026-07-29) — plataforma resolvida num ponto único.
    // Linhas anteriores a esta data ficam com platform NULL (sem backfill).
    const platform = getPlatform(); // "ios" | "android" | "web"

    // Insert via Supabase client (RLS validates user_hash = eng_hash_uid(auth.uid()))
    const insertPromise = (await cliente())
      .from("analytics_events")
      .insert({
        user_hash: userHash,
        event_name: params.event,
        screen: params.screen ?? null,
        module: params.module ?? null,
        child_age: params.childAge ?? null,
        metadata: enrichedMetadata,
        platform,
      })
      .then(({ error: insertError }) => {
        if (insertError && import.meta.env.DEV) {
          console.warn("[analytics] insert error:", insertError.message);
        }
      });

    // Quem navega logo depois precisa da gravação concluída, não em voo — mas
    // com teto: em rede ruim o insert pode pendurar, e analytics não pode
    // segurar cobrança. Estourado o teto, seguimos e o evento fica em voo
    // (perde-se se a navegação vier antes, que é o comportamento antigo).
    if (params.flush) {
      const flushed = await Promise.race([
        insertPromise.then(() => true),
        new Promise<boolean>((resolve) => setTimeout(() => resolve(false), FLUSH_TIMEOUT_MS)),
      ]);
      if (!flushed && import.meta.env.DEV) {
        console.warn(`[analytics] flush estourou ${FLUSH_TIMEOUT_MS}ms:`, params.event);
      }
    }

    // GA4 mirror — fire-and-forget, gated por consent dentro do gaEvent.
    // [29/10/2026] Só a lista permitida sai (ver paramsParaTerceiros); eventos
    // sensíveis não saem. `screen`/`module` também não: são rótulos de recurso.
    const seguro = paramsParaTerceiros(params.event, params.metadata ?? {});
    if (seguro) {
      gaEvent(params.event, { platform, ...seguro });
      // GA4 (web + nativo) — só os eventos de funil do mapa
      // FIREBASE_BUSINESS_EVENTS, com nome canônico e parâmetro de rótulo fixo.
      void mirrorToFirebase(params.event, platform, params.metadata ?? {});
    }



    if (import.meta.env.DEV) {
      console.debug(`[analytics] ${params.event}`, {
        screen: params.screen,
        module: params.module,
      });
    }
  } catch {
    // Never throw — analytics is best-effort
  }
}
